import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Play, TrendingUp, AlertTriangle, Zap, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Dashboard = () => {
  const [status, setStatus] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);
  
  const loadData = async () => {
    try {
      const [statusRes, analyticsRes, runsRes] = await Promise.all([
        axios.get(`${API}/status`),
        axios.get(`${API}/analytics/summary`),
        axios.get(`${API}/analysis/runs`)
      ]);
      
      setStatus(statusRes.data);
      setAnalytics(analyticsRes.data);
      setRecentRuns(runsRes.data.slice(0, 5));
      setLoading(false);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      toast.error('Fehler beim Laden der Daten');
      setLoading(false);
    }
  };
  
  const triggerAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      const response = await axios.post(`${API}/analysis/trigger`, {
        pages: []
      });
      
      toast.success('Analyse gestartet!');
      
      // Poll for updates
      setTimeout(() => {
        loadData();
        setIsAnalyzing(false);
      }, 5000);
    } catch (error) {
      console.error('Error triggering analysis:', error);
      toast.error('Fehler beim Starten der Analyse');
      setIsAnalyzing(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Dashboard</h1>
            <p className="text-slate-600">Übersicht für {status?.target_shop}</p>
          </div>
          <Button
            onClick={triggerAnalysis}
            disabled={isAnalyzing}
            size="lg"
            data-testid="trigger-analysis-btn"
            className="bg-slate-900 hover:bg-slate-800"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Analysiert...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Neue Analyse starten
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card data-testid="stat-total-issues">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Gefundene Issues</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">{analytics?.total_issues || 0}</div>
          </CardContent>
        </Card>
        
        <Card data-testid="stat-high-priority">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Hohe Priorität</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <div className="text-3xl font-bold text-red-600">
                {analytics?.issues_by_severity?.high || 0}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card data-testid="stat-optimizations">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Optimierungen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Zap className="w-6 h-6 text-blue-500" />
              <div className="text-3xl font-bold text-blue-600">
                {status?.total_optimizations || 0}
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card data-testid="stat-analysis-runs">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Analysen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-slate-900">
              {analytics?.total_analysis_runs || 0}
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Recent Analysis Runs */}
      <Card className="mb-8" data-testid="recent-runs-card">
        <CardHeader>
          <CardTitle>Letzte Analysen</CardTitle>
          <CardDescription>Übersicht der letzten Analyse-Durchläufe</CardDescription>
        </CardHeader>
        <CardContent>
          {recentRuns.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Noch keine Analysen durchgeführt. Starte die erste Analyse!
            </div>
          ) : (
            <div className="space-y-3">
              {recentRuns.map((run) => (
                <div
                  key={run.id}
                  data-testid={`analysis-run-${run.id}`}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-900">
                        {run.pages_analyzed || 0} Seiten analysiert
                      </div>
                      <div className="text-sm text-slate-500 flex items-center space-x-2">
                        <Clock className="w-3 h-3" />
                        <span>{new Date(run.timestamp).toLocaleString('de-DE')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-slate-900">
                        {run.issues_found || 0} Issues
                      </div>
                      <div className="text-xs text-slate-500">
                        {run.high_priority_issues || 0} kritisch
                      </div>
                    </div>
                    <Badge
                      variant={run.status === 'completed' ? 'default' : run.status === 'running' ? 'secondary' : 'destructive'}
                      data-testid={`run-status-${run.status}`}
                    >
                      {run.status === 'completed' ? 'Abgeschlossen' : run.status === 'running' ? 'Läuft...' : 'Fehler'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Issues by Type */}
      {analytics?.issues_by_type && analytics.issues_by_type.length > 0 && (
        <Card data-testid="issues-by-type-card">
          <CardHeader>
            <CardTitle>Issues nach Typ</CardTitle>
            <CardDescription>Verteilung der identifizierten Conversion-Probleme</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {analytics.issues_by_type.map((item) => (
                <div key={item.type} className="flex items-center justify-between">
                  <div className="text-sm text-slate-700 capitalize">
                    {item.type.replace(/_/g, ' ')}
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-32 bg-slate-200 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-slate-900 h-full rounded-full"
                        style={{ width: `${(item.count / analytics.total_issues) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-slate-900 w-8 text-right">
                      {item.count}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;